import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MessageSquare } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.email || !formData.password) {
      setError(t('auth.emailRequired'));
      return;
    }
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success(t('auth.loginSuccess'));
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || t('auth.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center space-x-2 mb-8">
          <MessageSquare className="w-10 h-10 text-emerald-600" />
          <span className="text-3xl font-bold text-slate-900">EVA AI</span>
        </div>

        <Card data-testid="login-form">
          <CardHeader className="text-center">
            <div className="flex justify-end mb-2">
              <LanguageSelector />
            </div>
            <CardTitle className="text-2xl">{t('auth.welcome')}</CardTitle>
            <CardDescription>{t('auth.loginSubtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6" data-testid="alert-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" data-testid="label-email">{t('auth.email')}</Label>
                <Input id="email" name="email" type="email" placeholder="name@example.com"
                  value={formData.email} onChange={handleChange} data-testid="input-email"
                  disabled={loading} autoComplete="email" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" data-testid="label-password">{t('auth.password')}</Label>
                <Input id="password" name="password" type="password" placeholder="••••••••"
                  value={formData.password} onChange={handleChange} data-testid="input-password"
                  disabled={loading} autoComplete="current-password" />
              </div>
              <Button type="submit" className="w-full" size="lg" disabled={loading} data-testid="button-login">
                {loading ? t('auth.loggingIn') : t('auth.login')}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <a href="/forgot-password" className="text-sm text-emerald-600 hover:underline" data-testid="link-forgot-password">
                {t('auth.forgotPassword')}
              </a>
            </div>
            <div className="mt-6 text-center text-sm text-slate-600">
              {t('auth.noAccount')}{' '}
              <a href="/signup" className="text-emerald-600 hover:underline" data-testid="link-signup">
                {t('auth.signup')}
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
